import React, { useState, useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { UPDATE_PRODUCTS_QUANTITY } from "api/graphql/mutations/product/UpdateProductsQuantity";
import { CREATE_INVENTORY_REQUEST } from "api/graphql/mutations/inventory/createInventoryRequest";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import debounce from 'lodash/debounce';
import { SELLER_PRODUCTS_QUERY } from "api/graphql/queries/product/GetInvProducts";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import { useTranslation } from "react-i18next";
import "./flatpickr-custom.css";
import "./inventory-styles.css";
import { FaTrashAlt, FaCalendarAlt } from "react-icons/fa";

// Types
type ProductOption = {
  value: string;
  label: string;
  image: string;
  seller_reference: string;
  stockQty: number;
};

type InventoryLine = {
  product: ProductOption | null;
  quantity: number;
};

const LOCAL_STORAGE_KEY = "inventory_request_form";

const CreateInventoryRequest: React.FC = () => {
  const { t } = useTranslation();
  const [sellerId, setSellerId] = useState<string>("");
  const [moveType, setMoveType] = useState<string>("");
  const [shippingType, setShippingType] = useState<string>("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<boolean>(false);
  const [moveTypeError, setMoveTypeError] = useState<boolean>(false);
  const [shippingTypeError, setShippingTypeError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Safely Load Inventory Lines from LocalStorage
  const [inventoryLines, setInventoryLines] = useState<InventoryLine[]>(() => {
    try {
      const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!savedData) return [{ product: null, quantity: 0 }];
      const parsedData = JSON.parse(savedData);
      return Array.isArray(parsedData.inventoryLines)
        ? parsedData.inventoryLines
        : [{ product: null, quantity: 0 }];
    } catch (error) {
      console.error("⚠ Error loading localStorage:", error);
      return [{ product: null, quantity: 0 }];
    }
  });

  const [fetchProducts, { data, loading: isLoadingProducts }] = useLazyQuery(SELLER_PRODUCTS_QUERY);
  const [updateProductsQuantity] = useMutation(UPDATE_PRODUCTS_QUANTITY);
  const [createInventoryRequestMutation] = useMutation(CREATE_INVENTORY_REQUEST);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Fetch Seller ID from localStorage
  useEffect(() => {
    try {
      const storedSeller = localStorage.getItem("seller");
      if (storedSeller) {
        const parsedSeller = JSON.parse(storedSeller);
        if (parsedSeller?.seller_id) {
          setSellerId(parsedSeller.seller_id.toString());
        }
      }
    } catch (error) {
      console.error("⚠ Error parsing seller data:", error);
    }
  }, []);

  // Process fetched products into options format
  useEffect(() => {
    if (data?.sellerProducts?.products) {
      const newOptions = data.sellerProducts.products.map((product: any) => ({
        value: product?.entityid?.toString() || "N/A",
        label: product?.name || t("inventory.unnamedProduct"),
        image: product?.image1?.url || "https://via.placeholder.com/50",
        stockQty: product?.stock_data?.qty || 0,
        seller_reference: product?.seller_reference || t("inventory.noReference"),
      }));
      
      setProductOptions(prevOptions => {
        // Filter out duplicates when appending new options
        const existingIds = new Set(prevOptions.map((p: ProductOption) => p.value));
        const uniqueNewOptions = newOptions.filter((p: ProductOption) => !existingIds.has(p.value));
        return [...prevOptions, ...uniqueNewOptions];
      });
    }
  }, [data, t]);

  // ✅ Load inventory lines from LocalStorage safely
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!savedData) return;

      const parsedData = JSON.parse(savedData);
      // Only load inventory lines from localStorage
      if (parsedData.inventoryLines) {
        setInventoryLines(Array.isArray(parsedData.inventoryLines) ? 
          parsedData.inventoryLines : [{ product: null, quantity: 0 }]);
      }
    } catch (error) {
      console.error("⚠ Error loading form data:", error);
    }
  }, []);

  // ✅ Save only inventory lines to LocalStorage whenever they change
  useEffect(() => {
    try {
      const formData = { inventoryLines };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formData));
    } catch (error) {
      console.error("⚠ Error saving form data:", error);
    }
  }, [inventoryLines]); // Only depend on inventoryLines

  // Keep track of all loaded options for stock calculations
  const [allLoadedOptions, setAllLoadedOptions] = useState<Map<string, ProductOption>>(new Map());

  // Track when the menu opens to reset pagination state
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);

  // Clear products and fetch new ones when shipping type or move type changes
  useEffect(() => {
    // Reset product options when shipping type or move type changes
    setProductOptions([]);
    setInventoryLines([{ product: null, quantity: 0 }]);
    
    // Only fetch products if shipping type is selected
    if (shippingType) {
      console.log("Fetching products based on shipping type:", shippingType, "and move type:", moveType);
      
      // Base filter based on shipping type
      let filter: any = shippingType === "FBW" 
        ? { 
            mode_expedition: 16500,     // FBW products
            type_id: "simple"           // Only simple products
          }
        : { 
            mode_expedition_neq: 16500, // drop (Easy Shipping) products
            type_id: "simple"           // Only simple products
          };
      
      // For "out" movement, add client-side filtering for products with stock > 0
      if (moveType === "out") {
        filter = {
          ...filter,
          qty_gt: 0  // Get products where qty > 0
        };
      }
      
      console.log("Using initial filter:", filter);
      
      fetchProducts({
        variables: {
          page: 1,
          pageSize: 20, 
          filter: filter
        },
      });
    }
  }, [shippingType, moveType, fetchProducts]); // Keep both dependencies to trigger refresh when move type changes
  
  // Process fetched products and filter by stock for "out" movements
  useEffect(() => {
    if (data?.sellerProducts?.products) {
      // Convert to product options
      let products = data.sellerProducts.products;
      
      // For "out" movement, filter products to only show those with stock > 0
      if (moveType === "out") {
        products = products.filter((product: any) => 
          product.stock_data && product.stock_data.qty > 0
        );
      }
      
      const newOptions = products.map((product: any) => ({
        value: product?.entityid?.toString() || "N/A",
        label: product?.name || t("inventory.unnamedProduct"),
        image: product?.image1?.url || "https://via.placeholder.com/50",
        stockQty: product?.stock_data?.qty || 0,
        seller_reference: product?.seller_reference || t("inventory.noReference"),
      }));
      
      setProductOptions(prevOptions => {
        // Filter out duplicates when appending new options
        const existingIds = new Set(prevOptions.map((p: ProductOption) => p.value));
        const uniqueNewOptions = newOptions.filter((p: ProductOption) => !existingIds.has(p.value));
        return [...prevOptions, ...uniqueNewOptions];
      });
    }
  }, [data, moveType, t]); // Depend on both data and moveType

  // Direct load products function for AsyncSelect
  const loadProducts = async (inputValue: string): Promise<ProductOption[]> => {
    try {
      console.log(`Loading products with search term: "${inputValue}"`);
      
      // Set search term
      setSearchTerm(inputValue);
      
      // Only proceed if shipping type is selected
      if (!shippingType) {
        return [];
      }
      
      // Base filter based on shipping type
      let filter: any = shippingType === "FBW"
        ? { 
            mode_expedition: 16500, // FBW products 
            type_id: "simple",      // Only simple products
            ...(inputValue && inputValue.length > 0 ? { name: inputValue } : {})
          }
        : { 
            mode_expedition_neq: 16500, // Drop shipping products
            type_id: "simple",          // Only simple products
            ...(inputValue && inputValue.length > 0 ? { name: inputValue } : {})
          };
      
      // For "out" movement, add qty_gt filter to only get products with stock
      if (moveType === "out") {
        filter = {
          ...filter,
          qty_gt: 0  // Get products where qty > 0
        };
      }
      
      console.log("Using filter:", filter);
      
      const { data } = await fetchProducts({
        variables: {
          page: 1, // Always start with page 1 for direct loads
          pageSize: 20,
          filter: filter
        },
      });
      
      if (data?.sellerProducts?.products) {
        const newOptions = data.sellerProducts.products.map((product: any) => ({
          value: product?.entityid?.toString() || "N/A",
          label: product?.name || t("inventory.unnamedProduct"),
          image: product?.image1?.url || "https://via.placeholder.com/50",
          stockQty: product?.stock_data?.qty || 0,
          seller_reference: product?.seller_reference || t("inventory.noReference"),
        }));
        
        // Set current page
        setCurrentPage(1);
        
        // Update options in state
        setProductOptions(newOptions);
        
        // Update loaded options map
        const newOptionsMap = new Map(allLoadedOptions);
        newOptions.forEach((option: ProductOption) => {
          newOptionsMap.set(option.value, option);
        });
        setAllLoadedOptions(newOptionsMap);
        
        return newOptions;
      }
      return [];
    } catch (error) {
      console.error("Error loading products:", error);
      return [];
    }
  };
  
  // Load more when scrolling to bottom
  const handleMenuScrollToBottom = async () => {
    try {
      const nextPage = currentPage + 1;
      console.log(`Menu scrolled to bottom, loading page ${nextPage}`);
      
      // Only proceed if shipping type is selected
      if (!shippingType) {
        return;
      }
      
      // Base filter based on shipping type
      let filter: any = shippingType === "FBW"
        ? { 
            mode_expedition: 16500, // FBW products 
            type_id: "simple",      // Only simple products
            ...(searchTerm && searchTerm.length > 0 ? { name: searchTerm } : {})
          }
        : { 
            mode_expedition_neq: 16500, // Drop shipping products
            type_id: "simple",          // Only simple products
            ...(searchTerm && searchTerm.length > 0 ? { name: searchTerm } : {})
          };
      
      // For "out" movement, add filter to only show products with stock > 0
      if (moveType === "out") {
        filter = {
          ...filter,
          qty_gt: 0  // Get products where qty > 0
        };
      }
      
      const { data } = await fetchProducts({
        variables: {
          page: nextPage,
          pageSize: 20,
          filter: filter
        },
      });
      
      if (data?.sellerProducts?.products) {
        const newOptions = data.sellerProducts.products.map((product: any) => ({
          value: product?.entityid?.toString() || "N/A",
          label: product?.name || t("inventory.unnamedProduct"),
          image: product?.image1?.url || "https://via.placeholder.com/50",
          stockQty: product?.stock_data?.qty || 0,
          seller_reference: product?.seller_reference || t("inventory.noReference"),
        }));
        
        // Update current page
        setCurrentPage(nextPage);
        
        // Add to options map
        const newOptionsMap = new Map(allLoadedOptions);
        newOptions.forEach((option: ProductOption) => {
          newOptionsMap.set(option.value, option);
        });
        setAllLoadedOptions(newOptionsMap);
        
        // Update options in state
        setProductOptions(prevOptions => {
          // Filter out duplicates
          const existingIds = new Set(prevOptions.map((p: ProductOption) => p.value));
          const uniqueNewOptions = newOptions.filter((p: ProductOption) => !existingIds.has(p.value));
          return [...prevOptions, ...uniqueNewOptions];
        });
        
        console.log(`Loaded ${newOptions.length} more products, total: ${productOptions.length + newOptions.length}`);
      }
    } catch (error) {
      console.error("Failed to load more products:", error);
    }
  };

  // ✅ Handle Product Change
  const handleProductChange = (selected: any, index: number) => {
    if (!selected) {
      const updatedLines = [...inventoryLines];
      updatedLines[index].product = null;
      setInventoryLines(updatedLines);
      return;
    }
    
    // Check if the selected product is already added in another line
    const isDuplicate = inventoryLines.some(
      (line, lineIndex) => 
        lineIndex !== index && 
        line.product?.value === selected.value
    );
    
    if (isDuplicate) {
      toast.error(t("inventory.errors.duplicateProduct"));
      return;
    }
    
    const updatedLines = [...inventoryLines];
    updatedLines[index].product = selected as ProductOption | null;
    setInventoryLines(updatedLines);
  };

  // ✅ Handle Quantity Change
  const handleQuantityChange = (index: number, qty: number) => {
    const updatedLines = [...inventoryLines];
    updatedLines[index].quantity = qty;
    setInventoryLines(updatedLines);
  };

  // ✅ Add New Line
  const addLine = () => {
    setInventoryLines([...inventoryLines, { product: null, quantity: 0 }]);
  };

  // ✅ Remove Line
  const removeLine = (index: number) => {
    setInventoryLines(inventoryLines.filter((_, i) => i !== index));
  };

  // ✅ Convert Date to "dd-mm-yyyy HH:MM:SS" Format
  const formatDate = (date: Date | undefined): string => {
    if (!date) return "";
    
    const pad = (num: number) => num.toString().padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };

  // ✅ Submit Inventory Request
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
  
    // Validate required fields
    let hasValidationError = false;
    
    if (!sellerId || inventoryLines.length === 0) {
      toast.error(t("inventory.errors.addProduct"));
      setIsSubmitting(false);
      return;
    }
    
    if (!moveType) {
      setMoveTypeError(true);
      hasValidationError = true;
    }

    if (!shippingType) {
      setShippingTypeError(true);
      hasValidationError = true;
    }
  
    // Only validate date if shipping type is FBW
    if (shippingType !== "drop" && !date) {
      setDateError(true);
      hasValidationError = true;
    }
    
    // Check for products with zero quantity
    const zeroQuantityLines = inventoryLines.filter(
      line => line.product !== null && line.quantity === 0
    );
    
    if (zeroQuantityLines.length > 0) {
      toast.error(t("inventory.errors.zeroQuantity"));
      setIsSubmitting(false);
      return;
    }
    
    if (hasValidationError) {
      if (!moveType) {
        document.getElementById("moveType")?.focus();
      } else if (shippingType !== "drop" && !date) {
        document.getElementById("datetimepicker")?.focus();
      }
      
      // Use a single toast for all validation errors
      toast.error(t("inventory.errors.fillRequired"));
      setIsSubmitting(false);
      return;
    }
  
    const validItems = inventoryLines.filter(
      (line) => line.product !== null && line.quantity > 0
    );    
  
    if (validItems.length === 0) {
      setIsSubmitting(false); // ✅ Important to reset
      toast.error(t("inventory.errors.selectProduct"));
      return;
    }    
  
    setDateError(false);
  
    const payload = {
      Inventory_Info: [
        {
          seller_id: sellerId,
          move_type: moveType,
          shipping_type: shippingType,
          date: shippingType === "drop" ? "" : formatDate(date),
        },
      ],
      Items: validItems.map((line) => ({
        ID: line.product!.value,
        Qty: line.quantity.toString(),
      })),
    };
  
    console.log("🚀 Submitting Inventory Request:", payload);
  
    try {
      // Create the inventory request through GraphQL
      const { data: inventoryData } = await createInventoryRequestMutation({
        variables: {
          seller_id: payload.Inventory_Info[0].seller_id,
          move_type: payload.Inventory_Info[0].move_type,
          stock_type: shippingType,
          date: payload.Inventory_Info[0].date,
          items: payload.Items
        }
      });
      
      if (inventoryData?.createInventoryRequest?.status !== 'success') {
        throw new Error(inventoryData?.createInventoryRequest?.message || 'Failed to create inventory request');
      }

      // If this is a drop shipping product (Easy Shipping), directly update the product quantities
      if (shippingType === "drop") {
        try {
          const updateQuantityItems = validItems.map(line => {
            const productId = line.product!.value;
            const changeQty = line.quantity;
            const currentQty = line.product!.stockQty || 0;
            
            // For "in" movement, add to current quantity
            // For "out" movement, subtract from current quantity
            const newQty = moveType === "in" 
              ? currentQty + changeQty 
              : Math.max(0, currentQty - changeQty);
            
            return {
              product_id: productId,
              quantity: newQty
            };
          });
          
          // Execute the product quantity update mutation
          console.log("🔄 Updating product quantities for drop shipping items:", updateQuantityItems);
          
          const result = await updateProductsQuantity({
            variables: {
              input: {
                items: updateQuantityItems
              }
            }
          });
          
          if (result.data?.updateProductsQuantity?.success) {
            console.log("✅ Product quantities updated successfully:", result.data.updateProductsQuantity);
          } else {
            console.warn("⚠️ Product quantity update partial success or failure:", result.data?.updateProductsQuantity?.message);
          }
        } catch (updateError) {
          console.error("❌ Error updating product quantities:", updateError);
          // We don't fail the entire operation if just the quantity update fails
          // Since the inventory request was already created
          toast.warning(t("inventory.warnings.partialUpdate"));
        }
      }
  
      toast.success(t("inventory.success.created"));
      localStorage.removeItem(LOCAL_STORAGE_KEY);
  
      setInventoryLines([{ product: null, quantity: 0 }]);
      setMoveType("");
      setShippingType("");
      setDate(undefined);
    } catch (error) {
      console.error("❌ Error submitting inventory request:", error);
      toast.error(t("inventory.errors.createFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };
  

  return (
    <div className="container mt-4">
      <div className="mb-4">
        <h2 className="mb-2 text-body">{t("inventory.title")}</h2>
        <p className="text-body-secondary">
          {t("inventory.subtitle")}
        </p>
        <hr className="my-3 border-secondary-subtle" />
      </div>

      <form onSubmit={handleSubmit} onKeyDown={(e) => {
        // Prevent form submission when pressing Enter in search fields
        const target = e.target as HTMLElement;
        if (e.key === 'Enter' && target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'submit') {
          e.preventDefault();
        }
      }}>
        <div className="card mb-4 border-0 shadow-sm bg-body">
          <div className="card-body">
            <div className="row align-items-center justify-content-start g-3">

              {/* Stock Type */}
              <div className="col-md-4">
                <div className="mb-3 mb-md-0">
                  <label className="form-label fw-bold" htmlFor="shippingType">
                    {t("inventory.stockType")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${shippingTypeError ? "is-invalid" : ""}`}
                    id="shippingType"
                    value={shippingType}
                    onChange={(e) => {
                      setShippingType(e.target.value);
                      if (e.target.value) setShippingTypeError(false);
                    }}
                  >
                    <option value="" disabled>{t("inventory.selectType")}</option>
                    <option value="FBW">{t("inventory.fbw")}</option>
                    <option value="drop">{t("inventory.easyShipping")}</option>
                  </select>
                  {shippingTypeError && (
                    <div className="invalid-feedback">
                      {t("inventory.errors.selectStockType")}
                    </div>
                  )}
                </div>
              </div>

              {/* Move Type */}
              <div className="col-md-4">
                <div className="mb-3 mb-md-0">
                  <label className="form-label fw-bold" htmlFor="moveType">
                    {t("inventory.movementType")} <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${moveTypeError ? "is-invalid" : ""}`}
                    id="moveType"
                    value={moveType}
                    onChange={(e) => {
                      setMoveType(e.target.value);
                      if (e.target.value) setMoveTypeError(false);
                    }}
                  >
                    <option value="" disabled>{t("inventory.selectType")}</option>
                    <option value="in">{t("inventory.stockIn")}</option>
                    <option value="out">{t("inventory.stockOut")}</option>
                  </select>
                  {moveTypeError && (
                    <div className="invalid-feedback">
                      {t("inventory.errors.selectMovementType")}
                    </div>
                  )}
                </div>
              </div>

              {/* Date Picker - Only show for FBW */}
              {shippingType !== "drop" && (
                <div className="col-md-4">
                  <div className="mb-0 position-relative">
                    <label className="form-label fw-bold" htmlFor="datetimepicker">
                      {t("inventory.reservedDate")} <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-body-tertiary">
                        <FaCalendarAlt />
                      </span>
                      <Flatpickr
                        id="datetimepicker"
                        className={`form-control ${dateError ? "is-invalid" : ""}`}
                        options={{
                          enableTime: true,
                          time_24hr: true,
                          dateFormat: "d/m/Y H:i",
                          disableMobile: true,
                          monthSelectorType: "static",
                          minDate: "today",
                        }}
                        value={date || undefined}
                        onChange={([selectedDate]) => {
                          setDate(selectedDate);
                          if (selectedDate) setDateError(false);
                        }}
                        placeholder={t("inventory.selectDateTime")}
                      />
                      {dateError && (
                        <div className="invalid-feedback">
                          {t("inventory.errors.selectReservedDate")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="mb-4">
          <table className="table table-hover align-middle" style={{ borderCollapse: "separate", borderSpacing: "0 12px", width: "100%" }}>
            <thead>
              <tr className="bg-body-secondary">
                <th style={{ width: "50%", border: "none", padding: "12px 20px", borderRadius: "8px 0 0 8px" }}>{t("inventory.product")}</th>
                <th style={{ width: "20%", border: "none", textAlign: "center" }}>{t("inventory.currentStock")}</th>
                <th style={{ width: "20%", border: "none", textAlign: "center" }}>
                {moveType === "in" 
                  ? t("inventory.stockAddition") 
                  : moveType === "out" 
                  ? t("inventory.stockReduction") 
                  : t("inventory.movement")}
                </th>
                <th style={{ width: "10%", border: "none", textAlign: "center", borderRadius: "0 8px 8px 0" }}>{t("inventory.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {inventoryLines.map((line, index) => {
                const selectedProduct = allLoadedOptions.get(line.product?.value ?? "");
                const currentStockQty = selectedProduct?.stockQty ?? 0;                
                
                // Calculate adjusted stock
                const adjustedStockQty = moveType === "in"
                  ? currentStockQty + line.quantity
                  : Math.max(currentStockQty - line.quantity, 0);
                
                const isQuantityExceedingStock = moveType === "out" && line.quantity > currentStockQty;
                
                return (
                  <tr key={index} style={{ 
                    backgroundColor: "var(--bs-body-bg)",
                    boxShadow: line.product ? "0 3px 10px rgba(0,0,0,0.08)" : "0 1px 3px rgba(0,0,0,0.05)",
                    borderRadius: "10px",
                    transition: "all 0.2s ease"
                  }}>
                    {/* Product Selector */}
                    <td style={{ border: "none", padding: "16px 20px", borderRadius: "10px 0 0 10px" }}>
                      <Select<ProductOption>
                        key={`product-select-${index}`}
                        value={line.product}
                        onChange={(selected) => handleProductChange(selected, index)}
                        options={productOptions.filter(option => 
                          // Filter out products that are already selected in other rows
                          !inventoryLines.some(
                            (line, lineIndex) => 
                              lineIndex !== index && 
                              line.product?.value === option.value
                          )
                        )}
                        onInputChange={(value, { action }) => {
                          console.log(`Search input changed to: "${value}" (action: ${action})`);
                          
                          // Directly trigger search or reset based on input value
                          loadProducts(value);
                          
                          return value;
                        }}
                        filterOption={() => true} // Skip built-in filtering, we'll handle it with API calls
                        onMenuOpen={() => {
                          setMenuIsOpen(true);
                          if (productOptions.length === 0) {
                            loadProducts("");
                          }
                        }}
                        onMenuClose={() => setMenuIsOpen(false)}
                        onMenuScrollToBottom={handleMenuScrollToBottom} // Enable lazy loading
                        isLoading={isLoadingProducts}
                        placeholder={t("inventory.searchProduct")}
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: "#ced4da",
                            boxShadow: "none",
                            "&:hover": { borderColor: "#86b7fe" },
                            minHeight: "42px",
                            borderRadius: "8px",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            borderRadius: "8px",
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isSelected 
                              ? "#0d6efd" 
                              : state.isFocused 
                                ? "#e9ecef" 
                                : base.backgroundColor,
                            padding: "10px 15px",
                          }),
                        }}
                        formatOptionLabel={(e: ProductOption) => (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div 
                              style={{ 
                                width: 45, 
                                height: 45, 
                                marginRight: 15, 
                                borderRadius: "8px", 
                                overflow: "hidden",
                                border: "1px solid #dee2e6"
                              }}
                            >
                              <img
                                src={e.image}
                                alt={e.label}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            </div>
                            <div>
                              <div style={{ fontWeight: 500, fontSize: "0.95rem" }}>{e.label}</div>
                              <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>REF: {e.seller_reference}</div>
                            </div>
                          </div>
                        )}
                        noOptionsMessage={() => t("inventory.noOptions")}
                      />
                    </td>

                    {/* Current Stock Display */}
                    <td style={{ border: "none", textAlign: "center", padding: "16px" }}>
                      {line.product ? (
                        <div className="d-inline-block px-3 py-2 rounded-1 stock-indicator">
                          {currentStockQty}
                        </div>
                      ) : "-"}
                    </td>

                    {/* Quantity Input with New Stock Display */}
                    <td style={{ border: "none", textAlign: "center", padding: "16px" }}>
                      {line.product ? (
                        <div className="d-flex align-items-center justify-content-center">
                          <div className="input-group" style={{ maxWidth: "120px" }}>
                            <input
                              type="number"
                              className={`form-control text-center ${isQuantityExceedingStock ? 'is-invalid' : ''}`}
                              min="0"
                              max={moveType === "out" ? currentStockQty : undefined}
                              value={line.quantity}
                              onChange={(e) => {
                                let value = parseInt(e.target.value, 10);
                                if (isNaN(value)) value = 0;
                                if (moveType === "out" && value > currentStockQty) {
                                  value = currentStockQty;
                                }
                                handleQuantityChange(index, value);
                              }}
                              disabled={!line.product}
                            />
                          </div>
                        </div>
                      ) : "-"}
                      
                      {isQuantityExceedingStock && (
                        <div className="invalid-feedback d-block mt-1 small">
                          {t("inventory.errors.exceedsStock")}
                        </div>
                      )}
                    </td>

                    {/* Delete Button */}
                    <td style={{ border: "none", width: "10%", textAlign: "center", padding: "10px" }}>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeLine(index)}
                        disabled={inventoryLines.length === 1}
                        title="Remove Line"
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Buttons Container */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button 
            type="button" 
            className="btn btn-outline-primary" 
            onClick={addLine}
          >
            {t("inventory.addProductButton")}
          </button>
          
          <button
            type="submit"
            className="btn btn-primary px-4"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("inventory.submitting")}
              </>
            ) : (
              t("inventory.submitButton")
            )}
          </button>
        </div>

      </form>
    </div>
  );
};

export default CreateInventoryRequest;
